import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomOnOrganizationCommand } from '../../../../core/networkMap/commands/ZoomOnOrganizationCommand';
import { MapStyle, useAppDispatch, useAppSelector } from '../../../../types';
import { LabelGreyButton } from '../components';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import CropFreeIcon from '@mui/icons-material/CropFree';
import TimelineIcon from '@mui/icons-material/Timeline';
import AddIcon from '@mui/icons-material/Add';
import PinIcon from '../../../../assets/icons/pin.svg?react';
import RemoveIcon from '@mui/icons-material/Remove';
import Satellite from '../../../../assets/img/satellite.jpg';
import Streets from '../../../../assets/img/street.jpg';
import { GreySquareButton } from '../../../design_system/inputs/buttons/GreySquareButton';
import { ButtonLabel, Control, MarginBottomBlock } from './components';
import {
    displayDegradationsQuery,
    displayPrQuery,
    displayRoadsQuery,
    selectedmapStyleQuery,
} from '../../../../core/networkMap/queries';
import { UpdateMapStyleCommand } from '../../../../core/networkMap/commands/UpdateMapStyleCommand';
import { SetDisplayDegradationsFromPersistenceCommand } from '../../../../core/networkMap/commands/SetDisplayDegradationsFromPersistenceCommand';
import { SetDisplayPrFromPersistenceCommand } from '../../../../core/networkMap/commands/SetDisplayPrFromPersistenceCommand';
import { SetDisplayRoadsFromPersistenceCommand } from '../../../../core/networkMap/commands/SetDisplayRoadsFromPersistenceCommand';
import { SetMapStyleFromPersistenceCommand } from '../../../../core/networkMap/commands/SetMapStyleFromPersistenceCommand';
import { UpdateDisplayDegradationsCommand } from '../../../../core/networkMap/commands/UpdateDisplayDegradationsCommand';
import { UpdateDisplayPrCommand } from '../../../../core/networkMap/commands/UpdateDisplayPrCommand';
import { useCommand } from '../../../store';
import { UpdateDisplayRoadsCommand } from '../../../../core/networkMap/commands/UpdateDisplayRoadsCommand';

export type Props = {
    handleZoomIn: () => void;
    handleZoomOut: () => void;
};

export const MapControls: FC<Props> = ({ handleZoomIn, handleZoomOut }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(SetDisplayPrFromPersistenceCommand());
        dispatch(SetDisplayRoadsFromPersistenceCommand());
        dispatch(SetDisplayDegradationsFromPersistenceCommand());
        dispatch(SetMapStyleFromPersistenceCommand());
    }, []);
    const displayPr = useAppSelector(displayPrQuery);
    const displayRoads = useAppSelector(displayRoadsQuery);
    const displayDegradations = useAppSelector(displayDegradationsQuery);
    const mapStyle = useAppSelector(selectedmapStyleQuery);

    const zoomOnOrganization = useCommand(ZoomOnOrganizationCommand);

    const handleChangeStyle = (value: MapStyle) => {
        dispatch(UpdateMapStyleCommand({ mapStyle: value }));
    };

    const handleDisplayPr = useCallback(() => {
        dispatch(UpdateDisplayPrCommand({ display: !displayPr }));
    }, [displayPr]);

    const handleDisplayRoads = useCallback(() => {
        dispatch(UpdateDisplayRoadsCommand({ display: !displayRoads }));
    }, [displayRoads]);

    const handleDisplayDegradations = useCallback(() => {
        dispatch(UpdateDisplayDegradationsCommand({ display: !displayDegradations }));
    }, [displayDegradations]);

    const [switchMapButtonLabel, switchMapButtonImageSource, toggleValue] =
        mapStyle === 'STREETS'
            ? [t('networkMap.navigation.satellite'), Satellite, 'SATELLITE']
            : [t('networkMap.navigation.streets'), Streets, 'STREETS'];

    return (
        <Control>
            <MarginBottomBlock>
                <LabelGreyButton label={t('networkMap.navigation.center')} handleClick={zoomOnOrganization}>
                    <CropFreeIcon />
                </LabelGreyButton>
            </MarginBottomBlock>
            <MarginBottomBlock>
                <GreySquareButton borderRadius="8px 8px 0 0" onClick={() => handleZoomIn()}>
                    <AddIcon />
                </GreySquareButton>
                <GreySquareButton borderRadius="0 0 8px 8px" onClick={() => handleZoomOut()}>
                    <RemoveIcon />
                </GreySquareButton>
            </MarginBottomBlock>
            <MarginBottomBlock>
                <GreySquareButton onClick={() => handleChangeStyle(toggleValue as MapStyle)}>
                    <img src={switchMapButtonImageSource} alt={switchMapButtonLabel} />
                    <ButtonLabel>{switchMapButtonLabel}</ButtonLabel>
                </GreySquareButton>
            </MarginBottomBlock>
            <MarginBottomBlock>
                <GreySquareButton onClick={handleDisplayPr} selected={displayPr}>
                    <PinIcon />
                </GreySquareButton>
            </MarginBottomBlock>
            <MarginBottomBlock>
                <GreySquareButton onClick={handleDisplayRoads} selected={displayRoads}>
                    <TimelineIcon />
                </GreySquareButton>
            </MarginBottomBlock>
            <MarginBottomBlock>
                <GreySquareButton onClick={handleDisplayDegradations} selected={displayDegradations}>
                    <BlurOnIcon />
                </GreySquareButton>
            </MarginBottomBlock>
        </Control>
    );
};
